import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import { formatClock } from '@/features/trips/format';
import type { TrackerHealthAlert } from './types';

/**
 * A tracker-health event in the alerts feed (slice 13f) — the FOURTH item shape,
 * beside the geofence crossing, the overspeed episode and the maintenance reminder.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * 🔴 THIS ROW GOES TO THE VEHICLE, NEVER TO A MAP.
 * ═════════════════════════════════════════════════════════════════════════════
 * A stale or lost tracker has no trustworthy location — the wire item carries no
 * `observedLat`/`observedLon`, and it must never be given one (the same trap
 * `MaintenanceRow` and ADR 0011 decision 2 name: live state standing in for an
 * observed fact is position 27 all over again). So this is a `Link` straight to
 * `/vehicles/:id`, not a select-and-centre-a-map button. It deliberately does NOT
 * call `onSelect`: in the dashboard's "recent alerts" that handler builds an
 * `/alerts?event=…&lat=…&lon=…` URL from `observedLat`/`observedLon` this item does
 * not have, so wiring it here would navigate to the map at `undefined` coordinates.
 * The honest destination is the vehicle, where the "Tracker silent since hh:mm"
 * line lives.
 *
 * COPY (EN + SO provisional, `trackerHealth.*`):
 *   - tracker_offline (warning) → "Tracker silent", silent-since time; "Resolved"
 *     with the recovery time once `endedAt` is set.
 *   - gps_fix_lost   (warning) → "GPS signal lost", likewise.
 *   - tracker_online (info)    → "Tracker back online" — the quiet recovery line
 *     (feed only; no push), so it never carries the attention weight.
 *
 * Read/unread is a leading dot + a weight change, never colour alone — the same
 * language as every other feed row. Tokens only; no hex literal enters this file.
 */
export function TrackerHealthRow({
  alert,
  plate,
  locale,
  timeZone,
  t,
}: {
  alert: TrackerHealthAlert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  t: TFunction;
}) {
  const online = alert.type === 'tracker_online';
  const gps = alert.type === 'gps_fix_lost';
  const resolved = alert.endedAt != null;
  // An OPEN warning (offline/gps still running) draws the eye; a resolved episode
  // and the info "back online" line are quiet, matching how OverspeedRow treats
  // `info`. There is no non-alerting offline, so an open one always has weight.
  const isAttention = !online && !resolved;

  const titleKey = online
    ? 'trackerHealth.online.title'
    : gps
      ? 'trackerHealth.gps.title'
      : 'trackerHealth.offline.title';

  const startedTime = formatClock(locale, Date.parse(alert.startedAt), timeZone);
  const observedTime = formatClock(locale, Date.parse(alert.observedAt), timeZone);
  const endedTime = alert.endedAt ? formatClock(locale, Date.parse(alert.endedAt), timeZone) : null;

  // The meta line: for a resolved offline/gps episode, the recovery time; for an
  // open one, the silent/lost-since time; for the info "back online" line, when it
  // came back. Never a fabricated single instant — the window is honest.
  const metaText = online
    ? t('alerts.observedAt', { time: observedTime })
    : resolved
      ? t('trackerHealth.resolvedAt', { time: endedTime ?? observedTime })
      : gps
        ? t('trackerHealth.gps.meta', { time: startedTime })
        : t('trackerHealth.offline.meta', { time: startedTime });

  const tagText = gps ? t('trackerHealth.tag.gps') : t('trackerHealth.tag.tracker');
  const tagClass = isAttention ? 'bg-raad-warn/18 text-raad-warn' : 'bg-raad-slate/12 text-raad-slate';

  const titleClass =
    isAttention && !alert.read ? 'font-semibold text-raad-text' : 'font-medium text-raad-muted';
  const dotClass = alert.read ? 'bg-transparent' : isAttention ? 'bg-raad-accent' : 'bg-raad-slate';

  return (
    <li>
      <Link
        to={`/vehicles/${alert.vehicleId}`}
        data-testid="tracker-health-row"
        data-type={alert.type}
        data-severity={alert.severity}
        data-alert-id={alert.id}
        className={[
          'block w-full rounded-xl border px-3.5 py-3 text-left transition-colors',
          isAttention
            ? 'border-raad-line-7 bg-raad-tile hover:border-raad-line-15'
            : 'border-raad-line-5 bg-raad-tile/60 hover:border-raad-line-12',
        ].join(' ')}
      >
        <div className="flex items-start gap-2.5">
          <span aria-hidden="true" className={['mt-1.5 size-2 flex-none rounded-full', dotClass].join(' ')} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className={['truncate text-sm', titleClass].join(' ')}>{t(titleKey)}</span>
              <span
                data-testid="tracker-health-tag"
                className={[
                  'flex-none rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                  tagClass,
                ].join(' ')}
              >
                {tagText}
              </span>
              {resolved ? (
                <span
                  data-testid="tracker-health-resolved"
                  className="flex-none rounded-md bg-raad-slate/12 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-raad-slate"
                >
                  {t('trackerHealth.resolved')}
                </span>
              ) : null}
              {!alert.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
            </div>

            {plate ? (
              <div className="mt-0.5 truncate text-[11.5px] font-semibold text-raad-dim">{plate}</div>
            ) : null}

            <div className="mt-1 text-[12px] text-raad-muted">{metaText}</div>
          </div>
        </div>
      </Link>
    </li>
  );
}
